import { z } from 'zod';
import { ecosystemSchema } from './ecosystem';
import { versionAdvisorySchema } from './advisory';

/** Identifies a package within an ecosystem (name as returned by deps.dev). */
export const packageKeySchema = z.object({
  system: ecosystemSchema,
  name: z.string().min(1).max(500),
});
export type PackageKey = z.infer<typeof packageKeySchema>;

/** Identifies an exact version of a package. */
export const versionKeySchema = packageKeySchema.extend({
  version: z.string().min(1).max(200),
});
export type VersionKey = z.infer<typeof versionKeySchema>;

/** One entry in a package's version list. */
export const packageVersionSchema = z.object({
  version: z.string().min(1),
  /** ISO-8601; deps.dev omits it for some old releases. */
  publishedAt: z.string().nullable(),
  isDefault: z.boolean(),
  isDeprecated: z.boolean(),
});
export type PackageVersion = z.infer<typeof packageVersionSchema>;

/** Response for `GET /api/v1/packages/:system/:name` — the version list. */
export const packageSummarySchema = z.object({
  packageKey: packageKeySchema,
  /** Newest-first. */
  versions: z.array(packageVersionSchema),
  /** The version deps.dev marks `isDefault` (usually "latest"), if any. */
  defaultVersion: z.string().nullable(),
});
export type PackageSummary = z.infer<typeof packageSummarySchema>;

/** External link attached to a version (homepage, repo, issue tracker...). */
export const packageLinkSchema = z.object({
  label: z.string(),
  url: z.string(),
});
export type PackageLink = z.infer<typeof packageLinkSchema>;

/** One SLSA provenance entry attached to a version. */
export const versionProvenanceSchema = z.object({
  sourceRepository: z.string().nullable(),
  commit: z.string().nullable(),
  url: z.string().nullable(),
  verified: z.boolean(),
});
export type VersionProvenance = z.infer<typeof versionProvenanceSchema>;

/** Dependent count breakdown for a version. */
export const versionDependentsSchema = z.object({
  total: z.number().int().nonnegative(),
  direct: z.number().int().nonnegative(),
  indirect: z.number().int().nonnegative(),
});
export type VersionDependents = z.infer<typeof versionDependentsSchema>;

/**
 * Response for `GET /api/v1/packages/:system/:name/versions/:version` —
 * everything Flow 1 shows: advisories (with fix ranges merged in from OSV),
 * deprecation, licenses, and the recommended upgrade.
 */
export const versionDetailSchema = z.object({
  versionKey: versionKeySchema,
  isDefault: z.boolean(),
  isDeprecated: z.boolean(),
  /** Non-null when the version is deprecated and a reason string is available. */
  deprecatedReason: z.string().nullable(),
  publishedAt: z.string().nullable(),
  licenses: z.array(z.string()),
  links: z.array(packageLinkSchema),
  advisories: z.array(versionAdvisorySchema),
  /** deps.dev recommended upgrade version (findings), when one exists. */
  recommendedVersion: z.string().nullable(),
  /** SLSA provenance entries attached to this version. */
  provenance: z.array(versionProvenanceSchema),
  /** Reverse-dependency counts from deps.dev. Null when unavailable. */
  dependents: versionDependentsSchema.nullable(),
  /** The related project id (e.g. "github.com/facebook/react"). Null when none. */
  relatedProjectId: z.string().nullable(),
});
export type VersionDetail = z.infer<typeof versionDetailSchema>;

/**
 * How a resolved dependency relates to the queried version. `DIRECT` is
 * declared in the package's own manifest; `INDIRECT` is pulled in transitively.
 * (deps.dev also emits `SELF` for the root node — dropped before it leaves the
 * Worker, so it never appears in the API response.)
 */
export const dependencyRelationSchema = z.enum(['DIRECT', 'INDIRECT']);
export type DependencyRelation = z.infer<typeof dependencyRelationSchema>;

/** One resolved dependency in a version's dependency graph. */
export const dependencyNodeSchema = z.object({
  system: ecosystemSchema,
  name: z.string().min(1),
  version: z.string().min(1),
  relation: dependencyRelationSchema,
  /** True when the dependency is bundled into the package itself. */
  bundled: z.boolean(),
  /**
   * The version requirement that pulled this dependency in (e.g. `^1.3.0`),
   * taken from the resolving edge. Null when deps.dev reports no requirement.
   */
  requirement: z.string().nullable(),
});
export type DependencyNode = z.infer<typeof dependencyNodeSchema>;

/**
 * A directed edge in the resolved dependency graph. `from`/`to` are node ids of
 * the form `${system}:${name}@${version}` — the same identity used for the root
 * and every entry in `dependencies`. `requirement` is the version constraint on
 * the edge (e.g. `^1.3.0`), null when deps.dev reports none.
 */
export const dependencyEdgeSchema = z.object({
  from: z.string().min(1),
  to: z.string().min(1),
  requirement: z.string().nullable(),
});
export type DependencyEdge = z.infer<typeof dependencyEdgeSchema>;

/**
 * Response for `GET /api/v1/packages/:system/:name/versions/:version/dependencies`
 * — the resolved dependency graph from deps.dev. Fetched on demand (not part of
 * the version detail) because the graph can be large.
 *
 * `dependencies` is the flat list (excludes the root); `root` + `edges` describe
 * the full node-edge graph (root included) for visualization. Node ids are
 * `${system}:${name}@${version}`.
 */
export const dependencyReportSchema = z.object({
  versionKey: versionKeySchema,
  /** The queried package itself (the SELF/root node of the graph). */
  root: versionKeySchema,
  /** Direct first, then indirect; each group sorted by name. Excludes root. */
  dependencies: z.array(dependencyNodeSchema),
  /** Directed edges over root + dependencies, by node id. */
  edges: z.array(dependencyEdgeSchema),
  counts: z.object({
    direct: z.number().int().nonnegative(),
    indirect: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
  }),
});
export type DependencyReport = z.infer<typeof dependencyReportSchema>;

/** Node id used throughout the dependency graph: `${system}:${name}@${version}`. */
export function dependencyNodeId(key: { system: string; name: string; version: string }): string {
  return `${key.system}:${key.name}@${key.version}`;
}
